import {
  Indicator,
  CheckboxProps as RadixCheckboxProps,
  CheckedState as RadixCheckedState,
  Root,
} from '@radix-ui/react-checkbox';
import { FieldInputProps, FieldMetaProps } from 'formik';
import { uniqueId } from 'lodash';
import { FocusEvent, useState } from 'react';
import tw from 'twin.macro';
import 'styled-components/macro';

import { CheckboxIndicator } from '../checkbox/CheckboxIndicator';
import { CheckboxTrigger } from '../checkbox/CheckboxTrigger';

export interface CheckboxProps
  extends Omit<RadixCheckboxProps, 'children' | 'onChange' | 'onCheckedChange'>,
    Partial<Omit<FieldMetaProps<boolean>, 'value'>>,
    Partial<Omit<FieldInputProps<boolean>, 'value' | 'checked' | 'onChange' | 'onBlur'>> {
  label?: React.ReactNode;

  // Faking a ChangeEvent for formik compatibility
  onChange?(event: { target: { type: 'checkbox'; name: string; checked: boolean } }): void;
}

export const Checkbox = ({
  label,
  disabled,
  name = '',
  className,
  onChange,
  onBlur,

  // Unused
  initialTouched,
  initialError,
  initialValue,
  error,
  touched,
  multiple,

  ...props
}: CheckboxProps) => {
  const [id] = useState(`checkbox-${uniqueId()}`);

  return (
    <div
      tw="inline-flex items-center gap-2 cursor-pointer leading-loose"
      css={[disabled && tw`cursor-auto`]}
      className={className}
    >
      <Root
        {...props}
        asChild
        id={id}
        // Using value !== false as we dont care about the 'indeterminate' state
        onCheckedChange={value => onChange?.({ target: { type: 'checkbox', name, checked: value !== false } })}
        onBlur={e => {
          (e as FocusEvent<HTMLInputElement>).target.name = name;
          onBlur?.(e);
        }}
        disabled={disabled}
        name={name}
      >
        <CheckboxTrigger>
          <Indicator asChild forceMount>
            <CheckboxIndicator isIndeterminate={props.checked === 'indeterminate'} />
          </Indicator>
        </CheckboxTrigger>
      </Root>

      {label && (
        <label htmlFor={id} tw="grow" css={[disabled && tw`text-gray-50`]}>
          {label}
        </label>
      )}
    </div>
  );
};

export type CheckedState = RadixCheckedState;
